import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser, faBars } from '@fortawesome/pro-solid-svg-icons';

import logoWhite from './logo-white.png';
import AuthContext from 'src/contexts/AuthContext';
import ResourcesContext from 'src/contexts/ResourcesContext';
import BlogContext from 'src/contexts/BlogContext';
import {
  elRed, textInverse, headerBackgroundDark
} from 'src/styles/colors';
import { TABLET_MIN_WIDTH } from 'src/styles/breakpoints';
import { DropdownChevron } from './NavigationDropdown';
import AccountMenu from './AccountMenu';
import {
  primaryNavigationItems, NAVIGATION_ACTIONS
} from './navigationConfig';
import { IconButton } from './elements';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 78px;
  padding: 0 5%;
  z-index: 999999;
  background-color: ${headerBackgroundDark};
  color: ${textInverse};

  img {
    height: 52px;
    width: auto;
  }
`;

const LinkContainer = styled.div`
  gap: 20px;
  display: flex;
  width: 50%;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  margin-top: 78px;
  font-weight: 400;
  font-size: 18px;
  background-color: ${headerBackgroundDark};
  position: absolute;
  top: 0;
  right: 0;
  z-index: 999999;

  @media (min-width: ${TABLET_MIN_WIDTH}px) {
    width: 25%;
  }
`;

const SectionLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-size: 16px;
`;

const SectionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const linkStyle = {
  textDecoration: 'none',
  color: textInverse,
};

const MobileMenu = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const [showMenuPanel, setShowMenuPanel] = useState(false);
  const [expandedSectionName, setExpandedSectionName] = useState(null);
  const [showAccountLinks, setShowAccountLinks] = useState(false);
  const {
    setInventoryFilterValues,
    setCurrentFilterPage,
    setCurrentItemOffset,
  } = useContext(ResourcesContext);
  const { clearBlogs } = useContext(BlogContext);

  const resetInventoryFilters = () => {
    setInventoryFilterValues({});
    setCurrentFilterPage(-1);
    setCurrentItemOffset(-1);
  };

  const actionHandlers = {
    [NAVIGATION_ACTIONS.RESET_INVENTORY_FILTERS]: resetInventoryFilters,
    [NAVIGATION_ACTIONS.CLEAR_BLOGS]: clearBlogs,
  };

  const closePanel = () => {
    setShowMenuPanel(false);
    setExpandedSectionName(null);
    setShowAccountLinks(false);
  };

  const handleItemClick = item => () => {
    if (item.action && actionHandlers[item.action]) {
      actionHandlers[item.action]();
    }
    if (item.to) closePanel();
  };

  const toggleSection = sectionName =>
    setExpandedSectionName(current =>
      current === sectionName ? null : sectionName
    );

  const renderChild = child => (
    <Link
      key={child.label}
      to={child.to}
      style={linkStyle}
      onClick={handleItemClick(child)}
    >
      {child.label}
    </Link>
  );

  const renderItem = item => (
    item.children ?
      <React.Fragment key={item.label}>
        <SectionLabel onClick={() => toggleSection(item.label)}>
          {item.label}
          <DropdownChevron isOpen={expandedSectionName === item.label} />
        </SectionLabel>
        {expandedSectionName === item.label &&
          <SectionLinks>
            {item.children.map(renderChild)}
          </SectionLinks>
        }
      </React.Fragment> :
      <Link
        key={item.label}
        to={item.to}
        style={linkStyle}
        onClick={handleItemClick(item)}
      >
        {item.label}
      </Link>
  );

  return (
    <MobileContainer>
      <Link to="/" onClick={resetInventoryFilters}>
        <img src={logoWhite} alt="Equiplinc" />
      </Link>
      <IconButton
        type="button"
        aria-label="Open menu"
        onClick={() => setShowMenuPanel(!showMenuPanel)}
      >
        <FontAwesomeIcon
          icon={faBars}
          size='2x'
          style={{ color: textInverse }}
        />
      </IconButton>
      {showMenuPanel &&
        <LinkContainer>
          {primaryNavigationItems({ isLoggedIn }).map(renderItem)}
          {isLoggedIn &&
            <>
              <IconButton
                type="button"
                aria-label="Account menu"
                onClick={() => setShowAccountLinks(!showAccountLinks)}
              >
                <FontAwesomeIcon
                  icon={faCircleUser}
                  size='2xl'
                  style={{ color: elRed }}
                />
              </IconButton>
              {showAccountLinks &&
                <SectionLinks>
                  <AccountMenu inverted onNavigate={closePanel} />
                </SectionLinks>
              }
            </>
          }
        </LinkContainer>
      }
    </MobileContainer>
  );
};

export default MobileMenu;
